import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, TextField, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ReactComponent as ForwardIcon } from 'src/icons/forward.svg';
import { ReactComponent as TuneIcon } from 'src/icons/tune.svg';

import {
  CHAT_SKILLS_MENU_TRIGGER,
  filterChatSkills,
  isSlashCommandInput,
  type ChatSkill,
} from '../chatSkills';
import {
  ChatInputShell,
  ChatInputToolbar,
  ChatSkillsTrigger,
  GdprBanner,
  InputContainer,
  InputWrapper,
} from '../styled';
import { GptForm } from '../types';
import { SlashSkillsMenu } from './SlashSkillsMenu';

export const InputBlock = ({
  onSubmit,
}: {
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}) => {
  const methods = useFormContext<GptForm>();
  const inputValue =
    useWatch({ control: methods.control, name: 'input' }) ?? '';
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const slashMenuOpen = isSlashCommandInput(inputValue);
  const queryAfterSlash = slashMenuOpen ? inputValue.slice(1) : '';

  const filteredSkills = useMemo(
    () => (slashMenuOpen ? filterChatSkills(queryAfterSlash) : []),
    [slashMenuOpen, queryAfterSlash],
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [queryAfterSlash]);

  useEffect(() => {
    if (
      highlightedIndex >= filteredSkills.length &&
      filteredSkills.length > 0
    ) {
      setHighlightedIndex(filteredSkills.length - 1);
    }
  }, [filteredSkills.length, highlightedIndex]);

  const applySkill = useCallback(
    (skill: ChatSkill) => {
      methods.setValue('input', skill.submitText, { shouldDirty: true });
      setHighlightedIndex(0);
    },
    [methods],
  );

  const handleCloseSlashMenu = useCallback(() => {
    if (slashMenuOpen) {
      methods.setValue('input', '', { shouldDirty: true });
    }
  }, [methods, slashMenuOpen]);

  const toggleSkillsMenu = useCallback(() => {
    if (slashMenuOpen) {
      handleCloseSlashMenu();
      return;
    }
    methods.setValue('input', CHAT_SKILLS_MENU_TRIGGER, { shouldDirty: true });
    setHighlightedIndex(0);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [handleCloseSlashMenu, methods, slashMenuOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (slashMenuOpen && filteredSkills.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex((i) =>
            Math.min(i + 1, Math.max(0, filteredSkills.length - 1)),
          );
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex((i) => Math.max(0, i - 1));
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const skill = filteredSkills[highlightedIndex];
          if (skill) applySkill(skill);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          handleCloseSlashMenu();
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey && methods.getValues('input')) {
        e.preventDefault();
        onSubmit();
      }
    },
    [
      applySkill,
      filteredSkills,
      handleCloseSlashMenu,
      highlightedIndex,
      methods,
      onSubmit,
      slashMenuOpen,
    ],
  );

  return (
    <InputContainer>
      <Controller
        name="input"
        control={methods.control}
        render={({ field: { onChange, value } }) => (
          <InputWrapper ref={anchorRef}>
            <SlashSkillsMenu
              open={slashMenuOpen}
              anchorEl={anchorRef.current}
              skills={filteredSkills}
              highlightedIndex={highlightedIndex}
              onSelect={(skill) => applySkill(skill)}
              onHighlightChange={setHighlightedIndex}
            />
            <ChatInputShell $active={slashMenuOpen}>
              <TextField
                inputRef={inputRef}
                variant="standard"
                sx={{ ' .MuiFormHelperText-root.Mui-error': { right: 0 } }}
                placeholder="Ask about your metrics"
                maxRows={8}
                multiline
                type="text"
                onKeyDown={handleKeyDown}
                value={value}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange(e.target.value)
                }
                fullWidth
                minRows={1}
                InputProps={{ disableUnderline: true }}
              />
              <ChatInputToolbar>
                <ChatSkillsTrigger
                  type="button"
                  $active={slashMenuOpen}
                  onClick={toggleSkillsMenu}
                  aria-label="Browse chat skills"
                  aria-expanded={slashMenuOpen}
                >
                  <TuneIcon aria-hidden />
                  Skills
                </ChatSkillsTrigger>
                {value ? (
                  <Button
                    sx={{
                      width: '52px',
                      height: '52px',
                      padding: '14px',
                      minWidth: 'auto',
                      flexShrink: 0,
                    }}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    <ForwardIcon />
                  </Button>
                ) : null}
              </ChatInputToolbar>
            </ChatInputShell>
            <GdprBanner>
              <Typography variant="body2" color="text.secondary" component="span">
                ⚠️ Responses are generated by AI and may contain errors. Always
                verify important information.
              </Typography>
            </GdprBanner>
          </InputWrapper>
        )}
      />
    </InputContainer>
  );
};
